import { useContext, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogTitle } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import DiscountList from '../../../components/discounts/DiscountsList';
import SttError from '../../../components/error/SttError';
import { AppContext } from '../../../sporttia/AppContext';
import { getErrorMessage } from '../../../lib/utils';
import { GetGroupDiscounts200 } from '../../../types/api/paths/Group';
import useScsService from '../../../services/ScsService';
import useActivityRatesService from '../../../services/ActivityRatesService';
import { GetActivityRateDiscounts200 } from '../../../types/api/paths/ActivityRate';

type ActivityRateDiscountListProps = {
    rateId: number;
};

/**
 * Componente para lista de descuentos en Actividades. Es básicamente lo mismo que GroupDiscountList, ya que por dentro una tarifa de actividad es un 'group'
 * He creado un componente aparte por si el día de mañana se desacoplan tarifas de actividades de la tabla grupos.
 * @param rateId
 * @constructor
 */
export default function ActivityRateDiscountList({
    rateId,
}: ActivityRateDiscountListProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [openDialog, setOpenDialog] = useState(false);

    const queryClient = useQueryClient();

    const activityRateService = useActivityRatesService();
    const getActivityRateDiscountsQuery =
        activityRateService.useGetActivityRateDiscounts(
            rateId,
            {
                rows: 1000,
            },
            {
                enabled: !!rateId,
            },
        );

    const scsService = useScsService();
    const getScDiscountsQuery = scsService.useGetScsDiscounts(
        cxt.sc!.id,
        { rows: 1000 },
        { enabled: openDialog } as any,
    );

    const createGroupDiscountMutation =
        activityRateService.useCreateActivityRateDiscount({
            onSuccess: (result: any) => {
                queryClient.setQueryData<GetGroupDiscounts200 | undefined>(
                    getActivityRateDiscountsQuery.queryKey,
                    (old: any) => {
                        if (!old) {
                            return undefined;
                        }

                        return {
                            ...old,
                            count: old.count + 1,
                            rows: [...old.rows, result.discount],
                        };
                    },
                );

                cxt.showMessage('S', t('group.discountAdded'));
                setOpenDialog(false);
            },
            onError: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
            },
        });

    const deleteGroupDiscountMutation =
        activityRateService.useDeleteActivityRateDiscount({
            onSuccess: (_result: any, variables: any) => {
                queryClient.setQueryData<
                    GetActivityRateDiscounts200 | undefined
                >(getActivityRateDiscountsQuery.queryKey, (old: any) => {
                    if (!old) {
                        return undefined;
                    }

                    return {
                        ...old,
                        count: old.count - 1,
                        rows: old?.rows.filter(
                            (row: any) => row.id !== variables.discountId,
                        ),
                    };
                });

                cxt.showMessage('S', t('group.discountDeleted'));
                setOpenDialog(false);
            },
            onError: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
            },
        });

    if (
        getActivityRateDiscountsQuery.isLoading ||
        createGroupDiscountMutation.isPending ||
        deleteGroupDiscountMutation.isPending
    ) {
        return (
            <Box display="flex" justifyContent="center" paddingTop={12}>
                <CircularProgress />
            </Box>
        );
    }

    if (getActivityRateDiscountsQuery.isError || getScDiscountsQuery.isError) {
        return <SttError />;
    }

    if (!getActivityRateDiscountsQuery.isSuccess) {
        return null;
    }

    return (
        <>
            <DiscountList
                discounts={getActivityRateDiscountsQuery.data.rows}
                onAdd={() => setOpenDialog(true)}
                onDelete={(discount) => {
                    deleteGroupDiscountMutation.mutate({
                        discountId: discount.id,
                        rateId,
                    });
                }}
            />

            <Dialog
                onClose={() => setOpenDialog(false)}
                open={openDialog}
                fullWidth
                maxWidth="xs"
            >
                {getScDiscountsQuery.isLoading && (
                    <Box display="flex" justifyContent="center" paddingY={12}>
                        <CircularProgress />
                    </Box>
                )}

                {getScDiscountsQuery.isSuccess && (
                    <>
                        <DialogTitle>{t('generic.discounts')}</DialogTitle>
                        <DiscountList
                            // Filtrar descuentos ya añadidos al grupo
                            discounts={getScDiscountsQuery.data.rows.filter(
                                (scDiscount) =>
                                    !getActivityRateDiscountsQuery.data.rows.some(
                                        (groupDiscount) =>
                                            groupDiscount.id === scDiscount.id,
                                    ),
                            )}
                            onSelect={(discount) => {
                                createGroupDiscountMutation.mutate({
                                    discountId: discount.id,
                                    rateId,
                                });
                            }}
                            showTitle={false}
                        />
                    </>
                )}
            </Dialog>
        </>
    );
}
